import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControlOptions, UntypedFormBuilder, Validators } from '@angular/forms';
import { WindInformationen } from '../../../domain/model/wind-informationen.model';
import { DialogService } from '../../../service/common/dialog/dialog.service';
import {
    AnlagenkennlinieSubFormComponent,
    AnlagenkennlinieSubFormData,
} from './anlagenkennlinie-sub-form/anlagenkennlinie-sub-form.component';
import { AbstractForm } from '../abstract-form.model';
import { FormGroupConfig } from '../form-config.model';
import { ErzeugungsanlageConfig } from '../erzeugungsanlage.config';
import { MatValidators } from '../../../common/material/components/mat-form-field-required.directive';
import { AnlagenkennlinienValidator } from '../validator/anlagenkennlinien.validator';
import { Feld } from '../../../domain/model/feld.enum';

@Component({
    selector: 'redispatch-wind-sub-form',
    templateUrl: './wind-sub-form.component.html',
    styleUrls: [
        './wind-sub-form.component.scss',
    ],
    standalone: false,
})
export class WindSubFormComponent extends AbstractForm<WindInformationen> implements OnChanges {
    public readonly NARBENHOEHE_MAX = 999999.99;
    public readonly NARBENHOEHE_PATTERN = new RegExp('^\\d{0,6}(\\.[\\d]{1,2})?$');
    public readonly KWH_MAX = 999999.999;
    public readonly KWH_PATTERN = new RegExp('^\\d{0,6}(\\.[\\d]{1,3})?$');
    public readonly GANZZALIG_PATTERN = new RegExp('^\\d*$');

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly ERROR_MIN_VALUES = ErzeugungsanlageConfig.ANLAGENKENNLINIE_MIN_ERROR;
    public readonly Feld = Feld;

    @Input({ required: true }) disabled: boolean;
    @Input({ required: true }) readonly: boolean;

    @Input({ required: true }) anlagenkennlinieRequired: boolean;
    @Input({ required: true }) isWindAnlage = false;

    private dialogService = inject(DialogService);

    constructor() {
        super(inject(UntypedFormBuilder));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.anlagenkennlinieRequired) {
            this.form.updateValueAndValidity();
        }
    }

    get formGroupConfig(): FormGroupConfig<WindInformationen> {
        return {
            anlagenHersteller: [
                {
                    value: '',
                    disabled: this.disabled || this.readonly,
                },
                [],
            ],
            anlagenTyp: [
                {
                    value: '',
                    disabled: this.disabled || this.readonly,
                },
                [
                    MatValidators.required,
                ],
            ],
            nabenhoehe: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    MatValidators.required,
                    Validators.max(this.NARBENHOEHE_MAX),
                    Validators.pattern(this.NARBENHOEHE_PATTERN),
                ],
            ],
            referenzertragWEA: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    MatValidators.required,
                    Validators.min(0),
                    Validators.pattern(this.GANZZALIG_PATTERN),
                ],
            ],
            nachtabsenkungswert: [
                {
                    value: null,
                    disabled: this.disabled || this.readonly,
                },
                [
                    Validators.max(this.KWH_MAX),
                    Validators.pattern(this.KWH_PATTERN),
                ],
            ],
            anlagenkennlinie: this.fb.group(
                AnlagenkennlinieSubFormComponent.formGroupConfig(this.disabled || this.readonly),
                AnlagenkennlinieSubFormComponent.formOptions(this.isAnlagenkennlinieRequired),
            ),
        };
    }

    get formOptions(): AbstractControlOptions | null {
        return {
            validators: AnlagenkennlinienValidator.required(this.isAnlagenkennlinieRequired),
        };
    }

    get disableAnlagenkennlinienButton(): boolean {
        return !this.isWindAnlage;
    }

    public isAnlagenkennlinieRequired = () => {
        return this.anlagenkennlinieRequired;
    };

    public sanitize(infos: WindInformationen): WindInformationen {
        return infos;
    }

    openAnlagenkennlinie(): void {
        this.dialogService.openDialog(AnlagenkennlinieSubFormComponent, {
            data: {
                form: this.f.anlagenkennlinie,
                disabled: this.disabled,
                readonly: this.readonly,
                isAnlagenkennlinieRequired: this.anlagenkennlinieRequired,
            } as AnlagenkennlinieSubFormData,
            disableClose: true,
        });
    }
}
